import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {Game} from "../Game";
import {FSWinTextStyle} from "./FeatureIntro";
import {MonsterSymbol} from "../reels/MonsterSymbol";
import {CountUp} from "./CountUp";
import {GameAssets} from "../GameAssets";
import {Sound} from "../sound/Sound";
import {GameModeNames} from "../server/GameModeNames";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

export class FeatureOutro extends PIXI.Container {

    private _fsWinText: PIXI.Text;
    private _spinsPlayed: PIXI.Text;
    private _finalMultiplier: PIXI.Text;
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _bgOverlay: PIXI.Graphics;
    private _bg: PIXI.Sprite;
    private _featureOutroCountUp: CountUp;
    private _isExited: boolean;

    constructor() {
        super();
        this.name = "FeatureOutro";

        this.init();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showOutro(clickCB: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2
            }),
            allowedWidthForTexts: number = 550,
            duration: number = 0.2;

        this._isExited = false;

        [this._button, this._bgOverlay, this._fsWinText, this._featureOutroCountUp, this._bg, this._spinsPlayed, this._finalMultiplier].forEach((item: PIXI.DisplayObject) => {
            item.alpha = 0;
            item.visible = true;
        });

        this._spinsPlayed.scale.set(1);
        this._spinsPlayed.text = Game.apiPlugin.translations.translate("Spins played: ${spinPlayed}").replace("${spinPlayed}", Game.model.gameData.freespinsPlayed.toString());
        if (this._spinsPlayed.width > allowedWidthForTexts) {
            const newScale: number = (allowedWidthForTexts / this._spinsPlayed.width);
            this._spinsPlayed.scale.set(newScale, newScale);
        }

        this._finalMultiplier.scale.set(1);
        this._finalMultiplier.text = Game.apiPlugin.translations.translate("Final multiplier: ") + "x" + Game.model.gameData.multiplier + "";
        if (this._finalMultiplier.width > allowedWidthForTexts) {
            const newScale: number = (allowedWidthForTexts / this._finalMultiplier.width);
            this._finalMultiplier.scale.set(newScale, newScale);
        }

        if (Game.isWinBelowStake() || Game.model.gameData.accumulatedRoundWin === 0) {
            this._fsWinText.visible = false;
        }

        this._clickCallback = clickCB;

        tl.add([
            this.handleOutroCountUp(),
            () => {
                Game.soundPlayer.playSound(Sound.FS_INTRO, 1, Number.MAX_VALUE);
                Game.soundPlayer.playSound(Sound.FS_STOP, 1);
            }
        ], 0);
        tl.to([this._bgOverlay, this._fsWinText, this._featureOutroCountUp, this._bg, this._spinsPlayed, this._finalMultiplier], {
            duration: duration,
            alpha: 1
        }, 0);

        // #433
        if (Game.model.gameData.wasFeatureBuy && Game.autoPlayPlugin.isAutoplayRound) {
            this._bgOverlay.interactive = true;
            this._bgOverlay.buttonMode = true;

            tl.to(this, {
                duration: 5
            }, 0);
            tl.add(() => {
                this._bgOverlay.interactive = false;
                this._bgOverlay.buttonMode = false;

                this.onButtonClick();
            });
        } else {
            tl.to(this._button, {
                duration: duration,
                alpha: 1
            }, 0);

            tl.add(() => {
                this._button.enable(true);
            });
        }

        return tl;
    }

    public handleOutroCountUp(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            totalWin: number = Game.model.gameData.accumulatedRoundWin;
        let winRatio: number = (totalWin / Game.model.gameData.playedBetValue),
            countUpDuration: number = 0;

        if (winRatio >= 5) {
            countUpDuration = 0.5;
        } else if (winRatio >= 3) {
            countUpDuration = 0.3;
        }

        Game.isPlayPickupHighlight = true;

        tl.addLabel("countUp");
        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
            tl.add((monster as MonsterSymbol).hideAmount(), "countUp");
        });

        // #39
        if (Game.isWinBelowStake()) {
            countUpDuration = 0;
        }

        if (countUpDuration > 0.01) {
            tl.add([
                () => {
                    this._featureOutroCountUp.stopAt(0);
                    this._featureOutroCountUp.preCalculateScale(0, totalWin);
                    this._featureOutroCountUp.getReady(310);

                    Game.soundPlayer.playSound(Sound.FS_COUNTUP, 1);
                },
                this._featureOutroCountUp.getCountAnimation(0, totalWin, countUpDuration)
            ], "countUp");
        } else {
            tl.add(() => {
                this._featureOutroCountUp.getReady(310);
                this._featureOutroCountUp.stopAt(totalWin);
            }, "countUp");
        }

        tl.add(() => {
            this._featureOutroCountUp.stopAt(totalWin);
            Game.keypadPlugin.setWin(totalWin, true, Game.isWinBelowStake());
        });

        tl.to(this, {
            duration: 0.3
        });

        tl.add(() => {
            const gameMode: string = Game.getCurrentGameMode();
            const isNextModeFreespin: boolean = Game.model.data.nextMode.includes(GameMode.FREESPIN);

            if (!isNextModeFreespin) {
                Game.isSFSActive = false;
            }

            if (gameMode === GameModeNames.SUPER_FREESPIN) {
                Game.soundPlayer.stopSound(Sound.SFS_LOOP);
                Game.soundPlayer.playSound(Sound.SFS_OUTRO, 1, Number.MAX_VALUE);
            } else if (gameMode === GameModeNames.FREESPIN) {
                Game.soundPlayer.stopSound(Sound.FS_LOOP);
                Game.soundPlayer.playSound(Sound.FS_OUTRO, 1, Number.MAX_VALUE);
            } else if (gameMode === GameModeNames.MAYHEM) {
                Game.soundPlayer.stopSound(Sound.MAYHEM_LOOP);
            }
        });

        return tl;
    }

    private init(): void {
        this.createBGOverlay();

        this._bg = new PIXI.Sprite(Game.assetLoader.getTextures("Mayhem_Intro/Mayhem_bg")[0]);
        this._bg.position.set(360);
        this._bg.anchor.set(0.5);
        this._bg.visible = false;

        this._fsWinText = new PIXI.Text(Game.apiPlugin.translations.translate("Total Win"), FSWinTextStyle);
        this._fsWinText.position.set(360, 230);
        this._fsWinText.anchor.set(0.5);
        this._fsWinText.visible = false;

        this._spinsPlayed = new PIXI.Text("", FSWinTextStyle);
        this._spinsPlayed.position.set(360, 440);
        this._spinsPlayed.anchor.set(0.5);
        this._spinsPlayed.visible = false;

        this._finalMultiplier = new PIXI.Text("", FSWinTextStyle);
        this._finalMultiplier.position.set(360, 490);
        this._finalMultiplier.anchor.set(0.5);
        this._finalMultiplier.visible = false;

        this._featureOutroCountUp = new CountUp(
            {
                fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
                fontSize: 40,
                tint: 0xffffff,
            },
            580,
            "FeatureOutroCountUp"
        );
        this._featureOutroCountUp.visible = false;

        this._button = new ContinueButton();
        this._button.addClickCallback(() => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;

        this.addChild(this._bg, this._fsWinText, this._spinsPlayed, this._finalMultiplier, this._featureOutroCountUp, this._button);

        this.onResize();
    }

    private onButtonClick(): void {
        if (this._isExited) {
            return;
        }
        this._isExited = true;

        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.5;

        this._button.enable(false);

        Game.backgroundElements.setMode(Game.model.data.nextMode);

        tl.to([this._button, this._bgOverlay, this._fsWinText, this._featureOutroCountUp, this._bg, this._spinsPlayed, this._finalMultiplier], {
            duration: duration,
            alpha: 0
        }, 0);
        tl.add(Game.scatterInventory.showSMeter(), 0);

        tl.add(() => {
            [this._button, this._bgOverlay, this._fsWinText, this._featureOutroCountUp, this._bg, this._spinsPlayed, this._finalMultiplier].forEach((item: PIXI.DisplayObject) => {
                item.visible = false;
                item.alpha = 1;
            });

            Game.soundPlayer.stopSound(Sound.FS_COUNTUP);
            Game.soundPlayer.stopSound(Sound.FS_INTRO);
            Game.soundPlayer.stopSound(Sound.FS_STOP);

            if (Game.model.data.mayhemSpin) {
                let fadeInAndStartMusic: string = Sound.MAIN_GAME_MUSIC;
                let isResume: boolean = false;
                if (Game.isSFSActive) {
                    fadeInAndStartMusic = Sound.SFS_LOOP;
                    isResume = true;
                } else if (Game.model.data.nextMode.includes(GameMode.FREESPIN)) {
                    fadeInAndStartMusic = Sound.FS_LOOP;
                    isResume = true;
                }

                if (fadeInAndStartMusic === Sound.MAIN_GAME_MUSIC) {
                    Game.soundPlayer.fadeInAndStartSound({
                        name: Sound.AS_SNARE,
                        from: 0,
                        to: 0,
                        duration: 4000,
                        repeat: Number.MAX_VALUE,
                        isResume: true
                    });

                    Game.soundPlayer.fadeInAndStartSound({
                        name: Sound.SNARE_PLAYER,
                        from: 0,
                        to: 0,
                        duration: 2000,
                        repeat: Number.MAX_VALUE,
                        isResume: true,
                    });
                } else {
                    Game.soundPlayer.stopSound(Sound.AS_SNARE);
                    Game.soundPlayer.stopSound(Sound.SNARE_PLAYER);
                }

                Game.soundPlayer.fadeInAndStartSound({
                    name: fadeInAndStartMusic,
                    from: 0,
                    to: 1,
                    duration: 2000,
                    repeat: Number.MAX_VALUE,
                    isResume
                });
            } else {
                Game.soundPlayer.stopSound(Sound.SFS_OUTRO);
                Game.soundPlayer.stopSound(Sound.FS_OUTRO);

                Game.soundPlayer.handleBGMusic(Game.getCurrentGameMode(), false);
                Game.soundPlayer.switchMusic(Sound.MAIN_GAME_MUSIC);
                Game.soundPlayer.playSound(Sound.MAIN_BG_AMBIENCE, 1, Number.MAX_VALUE);
            }

            this._clickCallback();
        });
    }

    private createBGOverlay(): void {
        this._bgOverlay = new PIXI.Graphics();

        this._bgOverlay.beginFill(0x000000, 0.7);
        this._bgOverlay.drawRect(0, 0, 1684, 1684);
        this._bgOverlay.position.set(-400);
        this._bgOverlay.name = "darkOverlay";
        this._bgOverlay.visible = false;

        // #433
        this._bgOverlay.on('pointerdown', () => {
            this.onButtonClick();
        });

        this.addChild(this._bgOverlay);
    }
}